import i18n, { isKeyInTranslation } from "../../i18n";
import { NavigatorType, NavigatorAvailability, NavigatorName } from "../../enums/command-hub/navigator";
import Mission from "./missions/mission";
import { MissionColor } from "../../utils/mission-color";
import ScheduleEntry, { ScheduleEntryTimes } from "./scheduling/schedule-entry";

interface RswNavigator {
  name: NavigatorName;
  type: NavigatorType;
  availability: NavigatorAvailability;
  hideInList: boolean;
  isWallFollowConfigurable: boolean;
  isRepeatConfigurable: boolean;
}

export const getNavigatorDescription = (navigator: RswNavigator | string) => {
  const navigatorName = typeof navigator === "string" ? navigator : navigator.name;
  const navigatorTranslationKey = `navigator-${navigatorName}`;
  const navigatorTranslation = i18n.t(navigatorTranslationKey);
  return navigatorTranslation !== navigatorTranslationKey ? navigatorTranslation : navigatorName;
};

export const getNavigatorDescriptionOrNull = (navigator?: RswNavigator | string | null) => {
  if (navigator == null) {
    return null;
  }

  const navigatorName = typeof navigator === "string" ? navigator : navigator.name;
  const navigatorTranslationKey = `navigator-${navigatorName}`;
  if (!isKeyInTranslation(navigatorTranslationKey)) {
    return null;
  }

  return i18n.t(navigatorTranslationKey);
};

export const getNavigatorDetailsDescriptionOrNull = (navigator: RswNavigator | string) => {
  const navigatorName = typeof navigator === "string" ? navigator : navigator.name;
  const navigatorTranslationKey = `navigator-description-${navigatorName}`;
  if (!isKeyInTranslation(navigatorTranslationKey)) {
    return null;
  }

  return i18n.t(navigatorTranslationKey);
};

export const navigators: RswNavigator[] = [
  {
    name: NavigatorName.DemoMode,
    type: NavigatorType.NavigatorWrapperOnly,
    availability: NavigatorAvailability.AdvancedModeOnly,
    hideInList: false,
    isWallFollowConfigurable: false,
    isRepeatConfigurable: false,
  },
  {
    name: NavigatorName.RepeatPathDemo,
    type: NavigatorType.NavigatorWrapperOnly,
    availability: NavigatorAvailability.AdvancedModeOnly,
    hideInList: false,
    isWallFollowConfigurable: false,
    isRepeatConfigurable: false,
  },
  {
    name: NavigatorName.OpenSpaceWallFollow,
    type: NavigatorType.CleaningNavigator,
    availability: NavigatorAvailability.AdvancedModeOnly,
    hideInList: false,
    isWallFollowConfigurable: true,
    isRepeatConfigurable: true,
  },
  {
    name: NavigatorName.RandomWallFollow,
    type: NavigatorType.CleaningNavigator,
    availability: NavigatorAvailability.Public,
    hideInList: false,
    isWallFollowConfigurable: true,
    isRepeatConfigurable: true,
  },
  {
    name: NavigatorName.SpotCleaning,
    type: NavigatorType.NavigatorWrapperOnly,
    availability: NavigatorAvailability.AdvancedModeOnly,
    hideInList: false,
    isWallFollowConfigurable: true,
    isRepeatConfigurable: false,
  },
  {
    name: NavigatorName.SubZones,
    type: NavigatorType.CleaningNavigator,
    availability: NavigatorAvailability.Public,
    hideInList: false,
    isWallFollowConfigurable: true,
    isRepeatConfigurable: true,
  },
  {
    name: NavigatorName.WallFollow,
    type: NavigatorType.CleaningNavigator,
    availability: NavigatorAvailability.Public,
    hideInList: false,
    isWallFollowConfigurable: false,
    isRepeatConfigurable: true,
  },
  {
    name: NavigatorName.ZoneCleaning,
    type: NavigatorType.CleaningNavigator,
    availability: NavigatorAvailability.Public,
    hideInList: true,
    isWallFollowConfigurable: false,
    isRepeatConfigurable: true,
  },
  {
    name: NavigatorName.Maintenance,
    type: NavigatorType.CleaningNavigator,
    availability: NavigatorAvailability.Public,
    hideInList: true,
    isWallFollowConfigurable: false,
    isRepeatConfigurable: false,
  },
];

export const getDefaultMissionName = (navigator: RswNavigator | string, roomMissions: Mission[], repeat: number, missionId?: string) => {
  const missionAlreadyExists = roomMissions.find((m) => m.id === missionId);
  let currentMode = "";
  let currentMissionNumber = 0;

  if (missionAlreadyExists) { // extrapolate mode and mission number in order to get the correct number 
    currentMode = i18n.t("rooms-cleaning");
    if (missionAlreadyExists.mode === navigatorZoneCleaningKey) {
      currentMode = i18n.t("zone-cleaning");
    } else if (missionAlreadyExists.mode === navigatorMaintenanceKey) {
      currentMode = i18n.t("maintenance-cleaning");
    }

    const missionNameMatch = missionAlreadyExists.name.match(/\d+/);
    currentMissionNumber = missionNameMatch ? parseInt(missionNameMatch[0], 10) : 1;
  }

  let newMode = i18n.t("rooms-cleaning");
  if (navigator === navigatorZoneCleaningKey) {
    newMode = i18n.t("zone-cleaning");
  } else if (navigator === navigatorMaintenanceKey) {
    newMode = i18n.t("maintenance-cleaning");
  }

  let missionsNumber = roomMissions.length;
  if (missionAlreadyExists && currentMode === newMode) { // mode stays the same, don't update mission number
    missionsNumber = currentMissionNumber;
  } else {
    if (navigator !== navigatorZoneCleaningKey && navigator !== navigatorMaintenanceKey) {
      missionsNumber = roomMissions.filter((m) => m.mode !== navigatorZoneCleaningKey && m.mode !== navigatorMaintenanceKey).length + 1;
    } else if (navigator === navigatorZoneCleaningKey) {
      missionsNumber = roomMissions.filter((m) => m.mode === navigatorZoneCleaningKey).length + 1;
    } else if (navigator === navigatorMaintenanceKey) {
      missionsNumber = roomMissions.filter((m) => m.mode === navigatorMaintenanceKey).length + 1;
    }
  }

  let cleaningRepeat = i18n.t("label-mission-repeat-once");
  if (repeat === 2) {
    cleaningRepeat = i18n.t("label-mission-repeat-twice");
  } else if (repeat === 3) {
    cleaningRepeat = i18n.t("label-mission-repeat-thrice");
  } else if (repeat === -1) {
    cleaningRepeat = i18n.t("label-mission-repeat-continuously");
  }

  const navigatorName = getNavigatorDescription(navigator);
  const repeatToUse = newMode === i18n.t("maintenance-cleaning") ? i18n.t("label-mission-repeat-once") : cleaningRepeat; // maintenance mode doesn't have repeat, but the name should maintain this structure

  return `${newMode} ${i18n.t("mission-name-default")} ${missionsNumber} - ${navigatorName} ${repeatToUse}`;
};

export const isDefaultMissionNameStructure = (missionName: string) => {
  const modePattern = `${i18n.t("rooms-cleaning")}|${i18n.t("zone-cleaning")}|${i18n.t("maintenance-cleaning")}`;
  const repeatPattern = `${i18n.t("label-mission-repeat-once")}|${i18n.t("label-mission-repeat-twice")}|${i18n.t("label-mission-repeat-thrice")}|${i18n.t("label-mission-repeat-continuously")}`;

  const regexPattern = `^(${modePattern}) ${i18n.t("mission-name-default")} (\\d+) - (.+) (${repeatPattern})$`;
  
  const regex = new RegExp(regexPattern);
  return regex.test(missionName);
};

export const getEntryScheduleTimeFromScheduledTimes = (scheduleEntry: ScheduleEntry, scheduleEntryTimesArray: ScheduleEntryTimes[]) => {
  return scheduleEntryTimesArray.find(entryTime => 
    scheduleEntry.weekdayFrom === entryTime.weekdayFrom &&
    scheduleEntry.weekdayTo === entryTime.weekdayTo &&
    scheduleEntry.timeFrom.hours === entryTime.timeFrom.hours && 
    scheduleEntry.timeFrom.minutes === entryTime.timeFrom.minutes &&
    scheduleEntry.timeTo.hours === entryTime.timeTo.hours && 
    scheduleEntry.timeTo.minutes === entryTime.timeTo.minutes
  );
};

export const getNextColorForMission = (numberOfPreviousMissions: number) => {
  const nextColorIndex = numberOfPreviousMissions % Object.values(MissionColor).length;
  return Object.values(MissionColor)[nextColorIndex];
}

export const navigatorZoneCleaningKey = "ZoneCleaning";
export const navigatorMaintenanceKey = "Maintenance";
export const navigatorOpenSpace = "OpenSpace";
export const navigatorOpenSpaceWallFollow = "OpenSpaceWallFollow";

export default RswNavigator;
